import type { CostsAndCharges, ProductGovernance, RegulatoryDocument } from 'models/streaming';
import type { HandledWorkspace } from 'state/forms/models';

export type RegulationActions =
  | RegulationQuoteRemoved
  | KidDocumentActions
  | TermsheetDocumentActions
  | TradeAuthorizationActions
  | ProductGovernanceActions
  | CostAndChargeActions;

type KidDocumentActions = KidDocumentReceived | KidDocumentReloaded | KidDocumentTimeout;
type TermsheetDocumentActions = TermsheetDocumentReceived | TermsheetDocumentReloaded | TermsheetDocumentTimeout;
type TradeAuthorizationActions = TradeAuthorizationReceived | TradeAuthorizationTimeout;
type ProductGovernanceActions =
  | ProductGovernanceAssessmentReceived
  | ProductGovernanceAssessmentReloaded
  | ProductGovernanceAssessmentTimeout
  | ProductGovernanceDocumentReceived
  | ProductGovernanceDocumentReloaded
  | ProductGovernanceDocumentTimeout;
type CostAndChargeActions = CostAndChargeReceived | CostAndChargeReloaded;

export function regulationQuoteRemoved(tabId: string, quoteId: string) {
  return {
    type: 'REGULATION_QUOTE_REMOVED' as const,
    tabId,
    quoteId,
  };
}

export type RegulationQuoteRemoved = ReturnType<typeof regulationQuoteRemoved>;

export function kidDocumentReceived(
  workspace: HandledWorkspace,
  tabId: string,
  quoteId: string,
  { listResources, rejections }: RegulatoryDocument,
  replacePreviousResources: boolean = false,
) {
  return {
    type: 'KID_DOCUMENT_RECEIVED' as const,
    workspace,
    tabId,
    quoteId,
    listResources,
    rejections,
    replacePreviousResources,
  };
}
export type KidDocumentReceived = ReturnType<typeof kidDocumentReceived>;

export function kidDocumentReloaded(
  workspace: HandledWorkspace,
  tabId: string,
  quoteId: string,
  { listResources, rejections }: RegulatoryDocument,
) {
  return {
    type: 'KID_DOCUMENT_RELOADED' as const,
    workspace,
    tabId,
    quoteId,
    listResources,
    rejections,
  };
}
export type KidDocumentReloaded = ReturnType<typeof kidDocumentReloaded>;

export function kidDocumentTimeout(workspace: HandledWorkspace, tabId: string, quoteId: string) {
  return {
    type: 'KID_DOCUMENT_TIMEOUT' as const,
    workspace,
    tabId,
    quoteId,
  };
}
export type KidDocumentTimeout = ReturnType<typeof kidDocumentTimeout>;

export function termsheetDocumentReceived(
  workspace: HandledWorkspace,
  tabId: string,
  quoteId: string,
  { listResources, rejections }: RegulatoryDocument,
  replacePreviousResources: boolean = false,
) {
  return {
    type: 'TERMSHEET_DOCUMENT_RECEIVED' as const,
    workspace,
    tabId,
    quoteId,
    listResources,
    rejections,
    replacePreviousResources,
  };
}
export type TermsheetDocumentReceived = ReturnType<typeof termsheetDocumentReceived>;

export function termsheetDocumentReloaded(
  workspace: HandledWorkspace,
  tabId: string,
  quoteId: string,
  { listResources, rejections }: RegulatoryDocument,
) {
  return {
    type: 'TERMSHEET_DOCUMENT_RELOADED' as const,
    workspace,
    tabId,
    quoteId,
    listResources,
    rejections,
  };
}
export type TermsheetDocumentReloaded = ReturnType<typeof termsheetDocumentReloaded>;

export function termsheetDocumentTimeout(workspace: HandledWorkspace, tabId: string, quoteId: string) {
  return {
    type: 'TERMSHEET_DOCUMENT_TIMEOUT' as const,
    workspace,
    tabId,
    quoteId,
  };
}
export type TermsheetDocumentTimeout = ReturnType<typeof termsheetDocumentTimeout>;

export function tradeAuthorizationReceived(workspace: HandledWorkspace, tabId: string, quoteId: string) {
  return {
    type: 'TRADE_AUTHORIZATION_RECEIVED' as const,
    workspace,
    tabId,
    quoteId,
  };
}
export type TradeAuthorizationReceived = ReturnType<typeof tradeAuthorizationReceived>;

export function tradeAuthorizationTimeout(tabId: string, quoteId: string) {
  return {
    type: 'TRADE_AUTHORIZATION_TIMEOUT' as const,
    tabId,
    quoteId,
  };
}
export type TradeAuthorizationTimeout = ReturnType<typeof tradeAuthorizationTimeout>;

export function productGovernanceAssessmentReceived(
  workspace: HandledWorkspace,
  tabId: string,
  quoteId: string,
  productGovernance: ProductGovernance | undefined,
) {
  return {
    type: 'PRODUCT_GOVERNANCE_ASSESSMENT_RECEIVED' as const,
    workspace,
    tabId,
    quoteId,
    productGovernance,
  };
}
export type ProductGovernanceAssessmentReceived = ReturnType<typeof productGovernanceAssessmentReceived>;

export function productGovernanceAssessmentReloaded(
  workspace: HandledWorkspace,
  tabId: string,
  quoteId: string,
  productGovernance: ProductGovernance,
) {
  return {
    type: 'PRODUCT_GOVERNANCE_ASSESSMENT_RELOADED' as const,
    workspace,
    tabId,
    quoteId,
    productGovernance,
  };
}
export type ProductGovernanceAssessmentReloaded = ReturnType<typeof productGovernanceAssessmentReloaded>;

export function productGovernanceAssessmentTimeout(workspace: HandledWorkspace, tabId: string, quoteId: string) {
  return {
    type: 'PRODUCT_GOVERNANCE_ASSESSMENT_TIMEOUT' as const,
    workspace,
    tabId,
    quoteId,
  };
}
export type ProductGovernanceAssessmentTimeout = ReturnType<typeof productGovernanceAssessmentTimeout>;

export function productGovernanceDocumentReceived(
  workspace: HandledWorkspace,
  tabId: string,
  quoteId: string,
  { listResources, rejections }: RegulatoryDocument,
  replacePreviousResources: boolean = false,
) {
  return {
    type: 'PRODUCT_GOVERNANCE_DOCUMENT_RECEIVED' as const,
    workspace,
    tabId,
    quoteId,
    listResources,
    rejections,
    replacePreviousResources,
  };
}
export type ProductGovernanceDocumentReceived = ReturnType<typeof productGovernanceDocumentReceived>;

export function productGovernanceDocumentReloaded(
  workspace: HandledWorkspace,
  tabId: string,
  quoteId: string,
  { listResources, rejections }: RegulatoryDocument,
) {
  return {
    type: 'PRODUCT_GOVERNANCE_DOCUMENT_RELOADED' as const,
    workspace,
    tabId,
    quoteId,
    listResources,
    rejections,
  };
}
export type ProductGovernanceDocumentReloaded = ReturnType<typeof productGovernanceDocumentReloaded>;

export function productGovernanceDocumentTimeout(workspace: HandledWorkspace, tabId: string, quoteId: string) {
  return {
    type: 'PRODUCT_GOVERNANCE_DOCUMENT_TIMEOUT' as const,
    workspace,
    tabId,
    quoteId,
  };
}
export type ProductGovernanceDocumentTimeout = ReturnType<typeof productGovernanceDocumentTimeout>;

export function costAndChargeReceived(
  workspace: HandledWorkspace,
  tabId: string,
  quoteId: string,
  { costsAndCharges, theoreticalValue }: CostsAndCharges,
) {
  return {
    type: 'COST_AND_CHARGE_RECEIVED' as const,
    workspace,
    tabId,
    quoteId,
    costsAndCharges,
    theoreticalValue,
  };
}
export type CostAndChargeReceived = ReturnType<typeof costAndChargeReceived>;

export function costAndChargeReloaded(
  workspace: HandledWorkspace,
  tabId: string,
  quoteId: string,
  { costsAndCharges, theoreticalValue }: CostsAndCharges,
) {
  return {
    type: 'COST_AND_CHARGE_RELOADED' as const,
    workspace,
    tabId,
    quoteId,
    costsAndCharges,
    theoreticalValue,
  };
}
export type CostAndChargeReloaded = ReturnType<typeof costAndChargeReloaded>;
